import React, { useState } from "react";
import { useCompany } from "../context/CompanyContext";
import { Sparkles, ChevronDown, ChevronUp, AlertTriangle, ThumbsUp, Info } from "lucide-react";

export default function OverviewSection() {
  const { company } = useCompany();
  const { takeaways } = company;
  const [expanded, setExpanded] = useState(false);
  return (
    <section className="bg-white border-b border-gray-200">
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <h2 className="text-lg md:text-2xl font-bold text-gray-900">{company.name} Overview</h2>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <Info size={12}/> The generated data is based on reviews and questionnaires provided by ReviewUK users.
        </p>

        <div className="mt-4 inline-flex items-center gap-1.5 bg-[#eef6f7] text-[#005f6b] text-xs font-medium px-3 py-1 rounded-full">
          <Sparkles size={12}/> Based on reviews, created with AI
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-1">
            <div className="text-sm font-semibold text-gray-700 mb-2">Rating Distribution</div>
            <div className="flex h-8 rounded-md overflow-hidden border border-gray-200">
              <div className="bg-[#e74c3c]" style={{ width: `${company.negative}%` }} title={`${company.negative}% negative`} />
              <div className="bg-[#f5a623]" style={{ width: `${100 - company.negative - company.positive}%` }} title="neutral" />
              <div className="bg-[#27ae60]" style={{ width: `${company.positive}%` }} title={`${company.positive}% positive`} />
            </div>
            <div className="flex justify-between mt-2 text-xs">
              <span className="text-[#e74c3c] font-semibold">{company.negative}% negative</span>
              <span className="text-[#27ae60] font-semibold">{company.positive}% positive</span>
            </div>
            <p className="text-sm text-gray-600 mt-4 leading-relaxed">
              {company.name} has a <span className="font-semibold text-gray-800">{company.rating} star</span> rating from {company.ratedReviews.toLocaleString()} customer reviews, with {company.recommend}% likely to recommend; {company.negative}% unfavorable and {company.positive}% positive, consumers mostly dissatisfied.
            </p>
          </div>

          <div>
            <div className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1.5">
              <Info size={14} className="text-[#007a8a]"/> Key Takeaways for Future Customers
            </div>
            <ul className="space-y-2">
              {takeaways.key.map((t, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-[#007a8a] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#007a8a] shrink-0"/>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1.5">
              <AlertTriangle size={14} className="text-[#e74c3c]"/> Negative Feedback / Risk Areas
            </div>
            <ul className="space-y-2">
              {takeaways.negative.map((t, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-[#e74c3c] mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e74c3c] shrink-0"/>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {expanded && (
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1.5">
              <ThumbsUp size={14} className="text-[#27ae60]"/> Positive Feedback
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{takeaways.positive}</p>
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 text-[#007a8a] hover:underline text-sm font-medium flex items-center gap-1"
        >
          {expanded ? "Hide full overview" : "View full overview"}
          {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
        </button>
      </div>
    </section>
  );
}
